import React, { useMemo } from "react";

const NICE_INTERVALS_M = [5, 10, 20, 25, 50, 100, 200, 250, 500];

export function printEngineeringMap() {
  const cleanup = () => document.body.classList.remove("printing-engineering-map");
  document.body.classList.add("printing-engineering-map");
  window.addEventListener("afterprint", cleanup, { once: true });
  window.print();
  window.setTimeout(cleanup, 1500);
}

export function engineeringGridInterval(scaleMPerPx = 1) {
  const targetMeters = Math.max(1, (Number(scaleMPerPx) || 1) * 45);
  return NICE_INTERVALS_M.find(value => value >= targetMeters) || 500;
}

function rotatePoint(x, y, originX, originY, degrees) {
  const angle = (Number(degrees) || 0) * Math.PI / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [originX + (x - originX) * cos - (y - originY) * sin, originY + (x - originX) * sin + (y - originY) * cos];
}

function Chainage({ berth, scale }) {
  if (!berth || !Number.isFinite(Number(berth.x1)) || !Number.isFinite(Number(berth.y1))) return null;
  const intervalM = engineeringGridInterval(scale) * 2;
  const lengthM = Number(berth.length_m) || Math.abs((Number(berth.x2) || 0) - Number(berth.x1)) * scale;
  const orientation = Number(berth.orientation_deg) || 0;
  const angle = orientation * Math.PI / 180;
  const normal = [-Math.sin(angle), Math.cos(angle)];
  const ticks = [];
  for (let meters = 0; meters <= lengthM + 0.001; meters += intervalM) ticks.push(meters);
  if (ticks[ticks.length - 1] !== lengthM && lengthM - ticks[ticks.length - 1] > intervalM * 0.35) ticks.push(lengthM);
  return <g data-testid="engineering-chainage">
    {ticks.map(meters => {
      const [x, y] = rotatePoint(Number(berth.x1) + meters / scale, Number(berth.y1), Number(berth.x1), Number(berth.y1), orientation);
      return <g key={`${berth.id || berth.name}-${meters}`}>
        <line x1={x - normal[0] * 6} y1={y - normal[1] * 6} x2={x + normal[0] * 9} y2={y + normal[1] * 9} stroke="#0F4C5C" strokeWidth="1" />
        <text x={x + normal[0] * 17} y={y + normal[1] * 17} textAnchor="middle" dominantBaseline="middle" fill="#0F4C5C" fontFamily="IBM Plex Mono, monospace" fontSize="8">{Math.round(meters)} m</text>
      </g>;
    })}
  </g>;
}

function BerthReference({ berth }) {
  if (!berth || ![berth.x1, berth.y1, berth.x2, berth.y2].every(value => Number.isFinite(Number(value)))) return null;
  const x1 = Number(berth.x1), y1 = Number(berth.y1), x2 = Number(berth.x2), y2 = Number(berth.y2);
  const corners = [[x1, y1], [x2, y1], [x2, y2], [x1, y2]].map(([x, y]) => rotatePoint(x, y, x1, y1, berth.orientation_deg));
  return <g data-testid={`engineering-berth-${berth.id || berth.name}`} pointerEvents="none">
    <polygon points={corners.map(point => point.join(",")).join(" ")} fill="#0E7490" fillOpacity="0.035" stroke="#5F8F9F" strokeWidth="1" strokeDasharray="6 3" />
    <line x1={corners[0][0]} y1={corners[0][1]} x2={corners[1][0]} y2={corners[1][1]} stroke="#0F4C5C" strokeWidth="2" />
    <text x={corners[3][0] + 7} y={corners[3][1] - 7} fill="#0F4C5C" fontFamily="IBM Plex Mono, monospace" fontSize="9" fontWeight="700">{berth.name || "BERTH"} · {berth.length_m || "?"} m</text>
  </g>;
}

export default function EngineeringDrawingFrame({
  width = 900,
  height = 600,
  scaleMPerPx = 1,
  berths = [],
  title = "TERMINAL OPERATIONS PLAN",
  subtitle = "LOCAL METRIC ENGINEERING GRID",
  showConfiguredBerths = true,
  children,
}) {
  const scale = Math.max(0.0001, Number(scaleMPerPx) || 1);
  const minorMeters = engineeringGridInterval(scale);
  const minorPx = minorMeters / scale;
  const majorPx = minorPx * 2;
  const verticalMinor = useMemo(() => Array.from({ length: Math.ceil(width / minorPx) + 1 }, (_, index) => index * minorPx), [minorPx, width]);
  const horizontalMinor = useMemo(() => Array.from({ length: Math.ceil(height / minorPx) + 1 }, (_, index) => index * minorPx), [height, minorPx]);
  const verticalMajor = useMemo(() => Array.from({ length: Math.ceil(width / majorPx) + 1 }, (_, index) => index * majorPx), [majorPx, width]);
  const horizontalMajor = useMemo(() => Array.from({ length: Math.ceil(height / majorPx) + 1 }, (_, index) => index * majorPx), [height, majorPx]);
  const scaleBarMeters = minorMeters * 2;
  const scaleBarPx = scaleBarMeters / scale;

  return <g data-testid="engineering-drawing-frame">
    <defs>
      <pattern id="engineering-no-go-hatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="8" stroke="#B91C1C" strokeWidth="1" opacity="0.2" /></pattern>
    </defs>
    <rect x="0" y="0" width={width} height={height} fill="#F8FAFC" />
    <g pointerEvents="none">
      {verticalMinor.map(x => <line key={`minor-v-${x}`} x1={x} y1="0" x2={x} y2={height} stroke="#D9E7EC" strokeWidth="0.6" />)}
      {horizontalMinor.map(y => <line key={`minor-h-${y}`} x1="0" y1={y} x2={width} y2={y} stroke="#D9E7EC" strokeWidth="0.6" />)}
      {verticalMajor.map((x, index) => <g key={`major-v-${x}`}><line x1={x} y1="0" x2={x} y2={height} stroke="#B9CDD5" strokeWidth="0.9" /><text x={x + 3} y="13" fill="#56717B" fontFamily="IBM Plex Mono, monospace" fontSize="8">{Math.round(index * scaleBarMeters)}m</text></g>)}
      {horizontalMajor.map((y, index) => <g key={`major-h-${y}`}><line x1="0" y1={y} x2={width} y2={y} stroke="#B9CDD5" strokeWidth="0.9" /><text x="3" y={y + 11} fill="#56717B" fontFamily="IBM Plex Mono, monospace" fontSize="8">{Math.round(index * scaleBarMeters)}m</text></g>)}
    </g>

    {children}

    <g pointerEvents="none">
      {showConfiguredBerths && berths.map(berth => <BerthReference key={`reference-${berth.id || berth.name}`} berth={berth} />)}
      {berths.map(berth => <Chainage key={berth.id || berth.name} berth={berth} scale={scale} />)}

      <g transform={`translate(24 ${height - 46})`}>
        <text x="0" y="-7" fill="#334E59" fontFamily="IBM Plex Mono, monospace" fontSize="8">SCALE BAR · LOCAL METRES</text>
        <rect x="0" y="0" width={scaleBarPx / 2} height="7" fill="#0F4C5C" />
        <rect x={scaleBarPx / 2} y="0" width={scaleBarPx / 2} height="7" fill="#FFFFFF" stroke="#0F4C5C" strokeWidth="1" />
        <text x="0" y="19" fill="#334E59" fontFamily="IBM Plex Mono, monospace" fontSize="8">0</text>
        <text x={scaleBarPx} y="19" textAnchor="end" fill="#334E59" fontFamily="IBM Plex Mono, monospace" fontSize="8">{scaleBarMeters} m</text>
      </g>

      <g transform="translate(24 30)">
        <line x1="0" y1="0" x2="34" y2="0" stroke="#0F4C5C" strokeWidth="1.5" />
        <path d="M34 0 L27 -4 L27 4 Z" fill="#0F4C5C" />
        <text x="39" y="3" fill="#0F4C5C" fontFamily="IBM Plex Mono, monospace" fontSize="9">X+</text>
        <line x1="0" y1="0" x2="0" y2="34" stroke="#0F4C5C" strokeWidth="1.5" />
        <path d="M0 34 L-4 27 L4 27 Z" fill="#0F4C5C" />
        <text x="7" y="34" fill="#0F4C5C" fontFamily="IBM Plex Mono, monospace" fontSize="9">Y+</text>
      </g>

      <g transform={`translate(${width - 310} ${height - 66})`}>
        <rect width="298" height="54" fill="#FFFFFF" fillOpacity="0.94" stroke="#0F4C5C" strokeWidth="1.2" />
        <line x1="0" y1="33" x2="298" y2="33" stroke="#0F4C5C" strokeWidth="0.7" />
        <line x1="218" y1="33" x2="218" y2="54" stroke="#0F4C5C" strokeWidth="0.7" />
        <text x="9" y="15" fill="#0F172A" fontFamily="IBM Plex Mono, monospace" fontSize="11" fontWeight="700">{title}</text>
        <text x="9" y="27" fill="#475569" fontFamily="IBM Plex Mono, monospace" fontSize="8">{subtitle}</text>
        <text x="9" y="47" fill="#475569" fontFamily="IBM Plex Mono, monospace" fontSize="8">OPERATIONAL DECISION AID · NOT FOR NAVIGATION</text>
        <text x="227" y="47" fill="#0F4C5C" fontFamily="IBM Plex Mono, monospace" fontSize="8">REV · CURRENT</text>
      </g>
      <rect x="8" y="8" width={width - 16} height={height - 16} fill="none" stroke="#0F4C5C" strokeWidth="1.4" />
      <rect x="12" y="12" width={width - 24} height={height - 24} fill="none" stroke="#7897A1" strokeWidth="0.6" />
    </g>
  </g>;
}
